import React, { useState, useEffect, useRef } from 'react';

const STEPS = [
    { type: 'cmd', text: 'whoami' },
    { type: 'out', text: 'raghul-saravanasundaram' },
    { type: 'cmd', text: 'cat role.txt' },
    { type: 'out', text: 'Software Engineer | Backend | Infrastructure' },
];

export default function Hero() {
    const [lines, setLines] = useState([]);
    const [currentText, setCurrentText] = useState('');
    const [stepIndex, setStepIndex] = useState(0);
    const [done, setDone] = useState(false);
    const cancelRef = useRef(false);
    const timerRef = useRef(null);

    useEffect(() => {
        cancelRef.current = false;

        if (stepIndex >= STEPS.length) {
            setDone(true);
            return;
        }

        const step = STEPS[stepIndex];

        if (step.type === 'out') {
            timerRef.current = setTimeout(() => {
                if (cancelRef.current) return;
                setLines(prev => [...prev, step]);
                setCurrentText('');
                setStepIndex(s => s + 1);
            }, 320);
            return () => {
                cancelRef.current = true;
                clearTimeout(timerRef.current);
            };
        }

        if (step.type === 'cmd') {
            let charIdx = 0;

            const typeNext = () => {
                if (cancelRef.current) return;
                charIdx++;
                setCurrentText(step.text.slice(0, charIdx));
                if (charIdx < step.text.length) {
                    timerRef.current = setTimeout(typeNext, 65);
                } else {
                    timerRef.current = setTimeout(() => {
                        if (cancelRef.current) return;
                        setLines(prev => [...prev, step]);
                        setCurrentText('');
                        setStepIndex(s => s + 1);
                    }, 480);
                }
            };

            timerRef.current = setTimeout(typeNext, stepIndex === 0 ? 900 : 220);

            return () => {
                cancelRef.current = true;
                clearTimeout(timerRef.current);
            };
        }
    }, [stepIndex]);

    const currentStep = stepIndex < STEPS.length ? STEPS[stepIndex] : null;
    const showPromptLine = !done && currentStep?.type === 'cmd';

    return (
        <section
            id="hero"
            className="relative min-h-screen flex items-center bg-[#0d1117] hero-dots px-6 pt-16"
        >
            <div className="max-w-3xl mx-auto w-full flex flex-col md:flex-row items-center gap-10 md:gap-16">

                {/* Terminal window */}
                <div className="flex-1 w-full max-w-lg terminal-glow rounded-lg">
                    {/* Chrome / title bar */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-[#21262d] rounded-t-lg border border-[#30363d] border-b-0">
                        <span className="w-3 h-3 rounded-full bg-[#ff5f57] inline-block" />
                        <span className="w-3 h-3 rounded-full bg-[#febc2e] inline-block" />
                        <span className="w-3 h-3 rounded-full bg-[#28c840] inline-block" />
                        <span className="ml-auto text-xs text-[#8b949e] font-mono select-none">
                            raghul@portfolio: ~
                        </span>
                    </div>

                    {/* Terminal body */}
                    <div className="bg-[#161b22] rounded-b-lg border border-[#30363d] border-t-0 p-6 font-mono text-sm leading-relaxed min-h-[210px]">
                        {lines.map((line, i) => (
                            <div key={i} className="mb-1">
                                {line.type === 'cmd' ? (
                                    <p>
                                        <span className="text-[#58a6ff] select-none">$ </span>
                                        <span className="text-[#e6edf3]">{line.text}</span>
                                    </p>
                                ) : (
                                    <p className="text-[#3fb950] pl-2">{line.text}</p>
                                )}
                            </div>
                        ))}

                        {/* Active typing line — only shown while a cmd is being typed */}
                        {showPromptLine && (
                            <div className="mb-1">
                                <span className="text-[#58a6ff] select-none">$ </span>
                                <span className="text-[#e6edf3]">{currentText}</span>
                                <span className="cursor-blink" />
                            </div>
                        )}

                        {/* Final idle prompt after all steps complete */}
                        {done && (
                            <div className="mt-2">
                                <span className="text-[#58a6ff] select-none">$ </span>
                                <span className="cursor-blink" />
                            </div>
                        )}

                        {/* Resume buttons — shown after animation completes */}
                        {done && (
                            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-[#30363d]">
                                <a
                                    href="/Resume-Raghul-Saravanasundaram.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-mono px-4 py-2 border border-[#3fb950] text-[#3fb950] rounded hover:bg-[#3fb950] hover:text-[#0d1117] transition-colors duration-200"
                                >
                                    $ open resume.pdf
                                </a>
                                <a
                                    href="/Resume-Raghul-Saravanasundaram.pdf"
                                    download
                                    className="text-xs font-mono text-[#8b949e] hover:text-[#3fb950] transition-colors duration-200"
                                >
                                    ↓ download
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Profile photo */}
                <div className="hidden md:flex flex-shrink-0 justify-center items-center">
                    <div className="w-56 h-56 rounded-full border-2 border-[#3fb950] overflow-hidden profile-glow bg-[#0d1117]">
                        <img
                            src="/images/profile3.png"
                            alt="Raghul"
                            className="w-full h-full object-cover object-top"
                            style={{ transform: 'translateY(15px)' }}
                        />
                    </div>
                </div>
            </div>

            {/* Scroll hint */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#8b949e] text-xs font-mono animate-bounce select-none">
                scroll ↓
            </div>
        </section>
    );
}
